import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MetaApp } from '../types/meta.types';
import { Shield, ExternalLink, Check } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ConnectAccountDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    apps: MetaApp[];
    selectedAppId: number | null;
    onAppSelect: (appId: number | null) => void;
    onConnect: () => void;
    isLoading: boolean;
}

export default function ConnectAccountDialog({
                                                 open,
                                                 onOpenChange,
                                                 apps,
                                                 selectedAppId,
                                                 onAppSelect,
                                                 onConnect,
                                                 isLoading,
                                             }: ConnectAccountDialogProps) {
    const activeApps = apps.filter(app => app.is_active);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        Conectar Conta Meta
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Selecione qual aplicação será usada para autenticação.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Lista de Apps */}
                    <div className="space-y-2">
                        <Label className="text-gray-700 font-medium">
                            Aplicação
                        </Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {activeApps.map((app) => (
                                <div
                                    key={app.id}
                                    onClick={() => onAppSelect(app.id)}
                                    className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                        selectedAppId === app.id
                                            ? 'border-[#0f172a] bg-[#0f172a]/5'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        {app.is_default && <Shield className="h-4 w-4 text-blue-600" />}
                                        <div>
                                            <span className="font-medium text-sm">{app.app_name}</span>
                                            <span className="text-xs text-gray-500 font-mono ml-2">
                                                ({app.app_id})
                                            </span>
                                            {app.is_default && (
                                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                                    Padrão
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {selectedAppId === app.id && (
                                        <Check className="h-5 w-5 text-[#0f172a]" />
                                    )}
                                </div>
                            ))}
                            {activeApps.length === 0 && (
                                <div className="p-4 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg">
                                    Nenhuma app disponível. Crie uma app primeiro.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Informações do Processo */}
                    <Alert>
                        <ExternalLink className="h-4 w-4" />
                        <AlertDescription className="text-sm text-gray-700">
                            <strong className="font-semibold">O que acontecerá:</strong>
                            <ol className="mt-2 ml-4 list-decimal space-y-1">
                                <li>Você será redirecionado para o Facebook</li>
                                <li>Faça login e autorize as permissões solicitadas</li>
                                <li>Suas páginas serão sincronizadas automaticamente</li>
                                <li>Você poderá usar as páginas para enviar broadcasts</li>
                            </ol>
                        </AlertDescription>
                    </Alert>

                    {/* Permissões que serão solicitadas */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="font-semibold text-sm text-gray-900 mb-2">
                            Permissões Solicitadas:
                        </h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                            <li className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#0f172a]" />
                                Ver lista de páginas
                            </li>
                            <li className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#0f172a]" />
                                Enviar mensagens pelo Messenger
                            </li>
                            <li className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#0f172a]" />
                                Gerenciar publicações
                            </li>
                            <li className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#0f172a]" />
                                Ver engajamento das páginas
                            </li>
                        </ul>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                        className="border-gray-300"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={onConnect}
                        disabled={isLoading || !selectedAppId}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        {isLoading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                Conectando...
                            </>
                        ) : (
                            <>
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Conectar com Facebook
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
